<template>
	<div>
		<ProspectiveTableHead
			:selected-rows="selectedRows"
			@table-head-action-executed="onChangeAndGetList"
		></ProspectiveTableHead>
		<div class="flex">
			<div class="flex flex_column w_100">
				<AgItem class="flex_1"
						:columnDefs="columnDefs"
						:rowData="rowData"
						@sortChanged="sortChanged"
						@selectionChanged="onSelectionChanged"
				></AgItem>
				<Pagination
					:totalNum="totalNum"
					:pageSize="pageSize"
					:sizeChange="sizeChange"
					:currentChange="currentChange"
					:currentPage="page"
				></Pagination>
			</div>
		</div>
	</div>
</template>

<script>

import AgItem from "@/components/templates/agGrid/AgItem.vue";
import Pagination from "@/components/templates/common/Pagination.vue";
import PaginationParent from "@/components/templates/common/PaginationParent.vue";
import {enumDef} from "@/common/enumDefs";
import {asLocalizedDate, asMoneyAmount} from "@/components/templates/agGrid/formatter/valueFormatters";
import {prospectiveReceivableApi} from "@/common/api/prospectiveReceivableApi";
import {mapGetters} from "vuex";
import ProspectiveTableHead from "@/components/templates/seller/prospective/ProspectiveTableHead.vue";
import {receivableApi} from "@/common/api/receivableApi";
import {navigationUtils} from "@/common/navigation/navigationUtils";
import {navigationPaths} from "@/common/navigation/navigationPaths";
import {navigationTabs} from "@/common/navigation/navigationTabs";
import {formatAsLocalizedDate, formatAsMoneyAmount} from "@/common/format";

export default {
	extends: PaginationParent,
	components: {
		ProspectiveTableHead,
		AgItem,
		Pagination
	},
	data() {
		return {
			rowData: null,
			columnDefs: [],
			sorting: [],
			selectedRows: []
		};
	},
	beforeMount() {
		this.setColumnDefs();
	},
	methods: {
		onSelectionChanged(data) {
			this.selectedRows = data;
		},
		sortChanged(sortData) {
			this.sorting = sortData;
			this.initPage();
			this.getList();
		},
		setColumnDefs() {
			this.columnDefs = [
				{
					field: "receivableId",
					sort: "desc",
					hide: true
				},
				{
					headerName: this.$t("seller.marketSounding.table.header.marketSoundingStatus"),
					minWidth: 180,
					maxWidth: 180,
					field: "opportunityStatus",
					cellRendererFramework: "EnumAsText",
					enumDef: enumDef.upcomingOpportunityStatus,
					sortable: true
				},
				{
					headerName: this.$t("seller.marketSounding.table.header.prospectiveStatus"),
					field: "receivableState",
					minWidth: 160,
					maxWidth: 190,
					cellRendererFramework: "EnumAsTextWithLinkAction",
					cellRendererParams: {
						enumDef: enumDef.allReceivablesState,
						customization: {
							[enumDef.allReceivablesState.Uploading]: {
								key: "common.data.allReceivablesState.uploadingConverted",
								linkAction: (_) => this.$router.push(navigationUtils.getRouteWithActiveTab(navigationPaths.seller.sell, navigationTabs.seller.sell.new))
							}
						}
					},
					sortable: false
				},
				{
					headerName: this.$t("common.type"),
					field: "receivableType",
					minWidth: 65,
					maxWidth: 80,
					cellRendererFramework: "EnumAsIcon",
					enumDef: enumDef.receivableType,
					sortable: true,
				},
				{
					headerName: this.$t("common.debtor"),
					field: "debtorName",
					minWidth: 200,
					sortable: true,
					nameType: "debtorName",
					idType: "debtorId",
					cellRendererFramework: "CompanyTem"
				},
				{
					headerName: this.$t("common.invoiceNumber"),
					headerClass: "align-right",
					field: "prospectiveInvoiceNumber",
					cellStyle: { "justify-content": "flex-end" },
					cellRendererFramework: "ValueWithComparisonSubtext",
					cellRendererParams: {
						comparisonValueField: "convertedInvoiceNumber",
						comparisonSubtextKeyGetter: (data) => this.getConvertedValueComparisonSubtextKey(data),
						compareFormatted: false
					},
					minWidth: 175,
					sortable: true,
				},
				{
					headerName: this.$t("common.invoiceDocument"),
					field: "invoiceDocumentFilename",
					minWidth: 175,
					cellRendererFramework: "FileLink",
					cellRendererParams: {
						fileIdField: "invoiceDocument",
					},
					sortable: true,
				},
				{
					headerName: this.$t("common.grossAmount"),
					headerClass: "align-right",
					field: "prospectiveReceivableAmount",
					valueFormatter: asMoneyAmount(),
					cellStyle: { "justify-content": "flex-end" },
					cellRendererFramework: "ValueWithComparisonSubtext",
					cellRendererParams: {
						comparisonValueGetter: (data) => formatAsMoneyAmount(data.convertedReceivableAmount),
						comparisonSubtextKeyGetter: (data) => this.getConvertedValueComparisonSubtextKey(data)
					},
					minWidth: 175,
					sortable: true,
				},
				{
					headerName: this.$t("common.ccy"),
					field: "currency",
					minWidth: 75,
					maxWidth: 100,
					sortable: true,
				},
				{
					headerName: this.$t("common.invoiceDate"),
					field: "prospectiveInvoiceDate",
					headerClass: "align-right",
					minWidth: 150,
					maxWidth: 200,
					valueFormatter: asLocalizedDate(),
					cellStyle: { "justify-content": "flex-end" },
					cellRendererFramework: "ValueWithComparisonSubtext",
					cellRendererParams: {
						comparisonValueGetter: (data) => formatAsLocalizedDate(data.convertedInvoiceDate),
						comparisonSubtextKeyGetter: (data) => this.getConvertedValueComparisonSubtextKey(data)
					},
					sortable: true,
				},
				{
					headerName: this.$t("common.dueDate"),
					headerClass: "align-right",
					field: "prospectiveDueDate",
					minWidth: 150,
					maxWidth: 200,
					valueFormatter: asLocalizedDate(),
					cellStyle: { "justify-content": "flex-end" },
					cellRendererFramework: "ValueWithComparisonSubtext",
					cellRendererParams: {
						comparisonValueGetter: (data) => formatAsLocalizedDate(data.convertedDueDate),
						comparisonSubtextKeyGetter: (data) => this.getConvertedValueComparisonSubtextKey(data)
					},
					sortable: true,
				},
				{
					headerName: this.$t("seller.marketSounding.table.header.endsIn"),
					headerClass: "align-right",
					field: "endTime",
					cellStyle: { "justify-content": "flex-end" },
					minWidth: 120,
					maxWidth: 120,
					cellRendererFramework: "TimeLeftCountdown",
					sortable: true,
					pinned: "right"
				},
				{
					headerName: this.$t("common.actions"),
					minWidth: 100,
					maxWidth: 100,
					sortable: false,
					cellRendererFramework: "ProspectiveTableOperation",
					cellRendererParams: {
						editFun: this.openEditForm,
						delFun: this.confirmDeletion,
						subFun: this.confirmSendToReview,
						convertFun: this.confirmConvert,
						withdrawFun: this.confirmWithdraw
					},
					pinned: "right",
				},
				{
					minWidth: 45,
					maxWidth: 45,
					headerCheckboxSelection: true,
					checkboxSelection: true,
					headerCheckboxSelectionFilteredOnly: true,
					pinned: "right",
				},
			];
		},
		getList() {
			let postData = {
				columns: [],
				filters: [],
				maxResults: this.maxResults,
				offset: this.offset,
				sorting: this.sorting,
			};
			prospectiveReceivableApi.inspection.listProspective(postData)
				.then(res => {
					this.setTotalNum(res.recordsTotal);
					this.rowData = res.data.map(item => {
						return {
							...item,
							invoiceNumber: item.prospectiveInvoiceNumber, // To make sure other depending components using simple "invoiceNumber" field still work
							expired: false
						};
					});
				})
				.catch(this.toast);
		},
		onChangeAndGetList() {
			this.onChange();
			this.getList();
		},
		openEditForm(data, showPdf) {
			this.$store.dispatch("setActionPdfList", [data]);

			if (!showPdf) {
				this.$store.dispatch("setActionActivePdf", {});
			} else  {
				this.$store.dispatch("setActionActivePdf", data);
			}

			this.$store.dispatch("setActionPdfDialog", {
				show: true,
				prospective: true,
				hideLeft: !showPdf,
				data: data
			});
		},
		confirmSendToReview(data) {
			let option = {
				title: this.$t("seller.marketSounding.submission.confirm.title"),
				msg: this.$t("seller.marketSounding.submission.confirm.text"),
				confirmFun: this.sendToReview,
				params: data.receivableId
			};
			this.confirmMeg(option, true);
		},
		sendToReview(id) {
			prospectiveReceivableApi.operation.submitReview(id)
				.then(() => {
					this.toastSuccess();
					this.onChangeAndGetList();
				})
				.catch(this.toast);
		},
		confirmDeletion(data) {
			let option = {
				title: "",
				msg: this.$t("seller.marketSounding.delete.confirm.text"),
				confirmFun: this.deleteProspectiveReceivable,
				params: data.receivableId
			};
			this.confirmMeg(option, true);
		},
		deleteProspectiveReceivable(id) {
			receivableApi.deleteOperation([id])
				.then(() => {
					this.toastSuccess();
					this.onChangeAndGetList();
				})
				.catch(this.toast);
		},
		confirmConvert(data) {
			let option = {
				title: this.$t("seller.marketSounding.convert.confirm.title"),
				msg: this.$t("seller.marketSounding.convert.confirm.text"),
				confirmFun: this.sendConvert,
				params: data.receivableId
			};
			this.confirmMeg(option, true);
		},
		sendConvert(id) {
			prospectiveReceivableApi.operation.convert(id)
				.then(() => {
					this.toastSuccess();
					this.onChangeAndGetList();
				})
				.catch(this.toast);
		},
		confirmWithdraw(data) {
			let option = {
				title: this.$t("seller.marketSounding.withdraw.confirm.title"),
				msg: this.$t("seller.marketSounding.withdraw.confirm.text"),
				confirmFun: this.sendWithdraw,
				params: data.receivableId
			};
			this.confirmMeg(option, true);
		},
		sendWithdraw(id) {
			prospectiveReceivableApi.operation.withdraw(id)
				.then(() => {
					this.toastSuccess();
					this.onChangeAndGetList();
				})
				.catch(this.toast);
		},
		onChange() {
			this.$emit("changed");
		},
		getConvertedValueComparisonSubtextKey(data) {
			if (data.opportunityConverted) {
				return "marketSounding.valueComparison.subtext.state.converted";
			} else {
				return null;
			}
		}
	},
	computed: {
		...mapGetters({ pdfDialogShow: "pdfDialogShow" }),
	},
	watch: {
		pdfDialogShow() {
			if (!this.pdfDialogShow) {
				this.getList();
			}
		},
	}

};
</script>
